import React, { useEffect, useState, useRef } from 'react';
import { ClearSocketMessageHandler, ConnectWebSocketForOverlay, DisconnectWebSocket, SetSocketMessageHandler } from './../../../utilities/BL/Zones';
import { GetConfig, SpinMachine, StopReel } from './../../../utilities/BL/Rollers';

export const RollersScreen = ({ screenId = 1, isStandalone = true, payload = null }) => {
    const getMediaUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http://') || path.startsWith('https://')) return path;
        const apiBase = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
        return `${apiBase}${path}`;
    };

    const [connectionStatus, setConnectionStatus] = useState('idle');
    const [statusMessage, setStatusMessage] = useState('¡SUERTE!');
    const [machineState, setMachineState] = useState('idle'); // idle, spinning, ended

    const [config, setConfig] = useState(null);
    const [theme, setTheme] = useState(null);
    const [machineScale, setMachineScale] = useState(100);
    const [identifyData, setIdentifyData] = useState(null);

    const [spinResult, setSpinResult] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);

    const spinResultRef = useRef(null);
    const pendingSpinRef = useRef(null);
    const currentStoppingReelRef = useRef(0);

    // Refs para animación
    const animationRefs = useRef([]);
    const rowPositions = useRef([]);
    const rowSpeeds = useRef([]);
    const rowStates = useRef([]); // 'STOPPING', 'STOPPED', 'RUNNING'
    const snapTargets = useRef([]);
    const stoppedSymbols = useRef([]);

    const lightsIntervalRef = useRef(null);

    const symbolsPerStrip = 50;
    const symbolWidth = 558; // Ancho fijo del rodillo
    const totalHeight = 558; // Alto fijo total de la imagen/caja

    useEffect(() => {
        loadConfig();
        if (isStandalone) {
            connectWS();
            // Escuchar botón físico antiestrés (tecla Space o Enter)
            window.addEventListener('keydown', handlePhysicalButton);
        }

        return () => {
            if (lightsIntervalRef.current) clearInterval(lightsIntervalRef.current);
            if (isStandalone) {
                window.removeEventListener('keydown', handlePhysicalButton);
                ClearSocketMessageHandler();
                DisconnectWebSocket();
            }
        };
    }, [screenId]);

    useEffect(() => {
        if (!isStandalone && payload) {
            handleIncomingMessage(payload);
        }
    }, [payload, isStandalone]);

    const loadConfig = async () => {
        const res = await GetConfig(screenId);
        if (res.success && res.data) {
            setConfig(res.data);
            setMachineScale(res.data.scale || 100);
            if (res.data.themeId) {
                fetchTheme(res.data.themeId);
            }
        }
    };

    const fetchTheme = async (themeId) => {
        const response = await fetch(`${process.env.REACT_APP_API_URL}Rollers/GetTheme/${themeId}`);
        const data = await response.json();
        if (data.success) {
            setTheme(data.data);
            startIdleLights(data.data.bulbColors);

            // Si había un spin en cola esperando a que se cargara el tema, lo ejecutamos
            if (pendingSpinRef.current) {
                const spinData = pendingSpinRef.current;
                pendingSpinRef.current = null;
                setTimeout(() => {
                    startSpin(spinData, data.data);
                }, 100);
            }
        }
    };

    const connectWS = async () => {
        const overlayType = `rollers:screen:${screenId}`;
        await ConnectWebSocketForOverlay(
            overlayType,
            (status) => setConnectionStatus(status),
            (toastMsg) => console.log(`[Rollers WS] ${toastMsg}`)
        );
        SetSocketMessageHandler(handleIncomingMessage);
    };

    const handleIncomingMessage = (message) => {
        if (!message || !message.type) return;

        const type = message.type;
        const msgData = message.data || message.payload || {};

        switch (type.toLowerCase()) {
            case 'rollers.prepare':
                loadConfig();
                resetMachine();
                break;
            case 'rollers.spin':
                startSpin(msgData);
                break;
            case 'rollers.stop_reel':
                stopReel(msgData.reelIndex);
                break;
            case 'rollers.identify':
                setIdentifyData(msgData.message || 'PANTALLA IDENTIFICADA');
                setTimeout(() => setIdentifyData(null), 3000);
                break;
            default:
                break;
        }
    };

    const handlePhysicalButton = async (e) => {
        // Disparador del botón antiestrés físico (Espacio o Enter)
        if (e.key === ' ' || e.key === 'Enter') {
            const count = config?.rollersCount || 3;
            if (machineState === 'idle') {
                try {
                    await SpinMachine({
                        targetScreenId: screenId,
                        playerName: "Botón Antiestrés",
                        playerContact: "Físico USB"
                    });
                } catch (err) {
                    console.error("Fallo al disparar tiro físico:", err);
                }
            } else if (machineState === 'spinning') {
                if (currentStoppingReelRef.current < count) {
                    try {
                        await StopReel(screenId, currentStoppingReelRef.current);
                    } catch (err) {
                        console.error("Error al parar rodillo físicamente:", err);
                    }
                }
            }
        }
    };

    const resetMachine = () => {
        setIsPlaying(false);
        setMachineState('idle');
        setStatusMessage('¡SUERTE!');
        currentStoppingReelRef.current = 0;
        animationRefs.current.forEach(cancelAnimationFrame);
        animationRefs.current = [];
        rowPositions.current = [];
        rowSpeeds.current = [];
        rowStates.current = [];
        // Forzar render a posición inicial (todas alineadas al primer símbolo)
        const count = config?.rollersCount || 3;
        for (let i = 0; i < count; i++) {
            const strip = document.getElementById(`rollers-strip-${i}`);
            if (strip) strip.style.transform = `translateX(0px)`;
        }
    };

    // ==========================================
    // LUCES DE CASINO DINÁMICAS
    // ==========================================
    const startIdleLights = (colorsString) => {
        if (lightsIntervalRef.current) clearInterval(lightsIntervalRef.current);
        const colors = colorsString ? colorsString.split(',') : ['#ffff00', '#ff0000'];
        const bulbs = document.querySelectorAll('.rollers-light-bulb');

        lightsIntervalRef.current = setInterval(() => {
            bulbs.forEach((bulb, idx) => {
                const color = colors[(idx + Math.floor(Date.now() / 300)) % colors.length];
                bulb.style.backgroundColor = color;
                bulb.style.boxShadow = `0 0 15px ${color}, inset 0 0 5px #fff`;
            });
        }, 150);
    };

    const startSpinningLights = (colorsString) => {
        if (lightsIntervalRef.current) clearInterval(lightsIntervalRef.current);
        const colors = colorsString ? colorsString.split(',') : ['#ff00ff', '#00ffff', '#ffff00'];
        const bulbs = document.querySelectorAll('.rollers-light-bulb');

        lightsIntervalRef.current = setInterval(() => {
            bulbs.forEach((bulb, idx) => {
                const color = colors[(idx + Math.floor(Date.now() / 80)) % colors.length];
                bulb.style.backgroundColor = color;
                bulb.style.boxShadow = `0 0 25px ${color}, inset 0 0 8px #fff`;
            });
        }, 60);
    };

    // ==========================================
    // LÓGICA DE GIRO
    // ==========================================
    const startSpin = (spinData, overrideTheme = null) => {
        const activeTheme = overrideTheme || theme;
        const count = config?.rollersCount || 3;
        const images = activeTheme?.rollerImages || [];

        if (images.length === 0) {
            pendingSpinRef.current = spinData;
            return;
        }

        if (isPlaying) return;
        setIsPlaying(true);
        setMachineState('spinning');
        setStatusMessage('GIRANDO...');
        setSpinResult(spinData);
        spinResultRef.current = spinData;
        currentStoppingReelRef.current = 0;

        // Iniciar luces de giro rápido
        startSpinningLights(activeTheme?.bulbColors);

        // Configuración de velocidades según la velocidad de giro configurada (baseTimeMs)
        let baseSpeeds = [14, 18, 22, 26, 30]; // Normal
        if (spinData.baseTimeMs === 3500) {
            baseSpeeds = [9, 11, 13, 15, 17]; // Lento
        } else if (spinData.baseTimeMs === 1500) {
            baseSpeeds = [22, 27, 32, 37, 42]; // Rápido
        }

        if (spinData.speeds && spinData.speeds.length >= count) {
            baseSpeeds = spinData.speeds;
        }

        // Configurar arrays de estado por rodillo
        for (let i = 0; i < count; i++) {
            rowPositions.current[i] = 0;
            rowSpeeds.current[i] = baseSpeeds[i] || (14 + i * 4);
            rowStates.current[i] = 'RUNNING';
            
            // Iniciar animación del rodillo
            animateReel(i, images);
        }
    };

    const animateReel = (rowIndex, images) => {
        if (rowStates.current[rowIndex] === 'STOPPED') return;

        const sequenceWidth = images.length * symbolWidth;

        if (rowStates.current[rowIndex] === 'STOPPING') {
            const distance = Math.abs(rowPositions.current[rowIndex] - snapTargets.current[rowIndex]);

            if (distance <= rowSpeeds.current[rowIndex]) {
                rowPositions.current[rowIndex] = snapTargets.current[rowIndex];
                rowStates.current[rowIndex] = 'STOPPED';

                // Verificar si todos terminaron
                if (rowStates.current.every(st => st === 'STOPPED')) {
                    finishSpin();
                }
                return;
            }

            // Desaceleración progresiva (ease-out) proporcional a la distancia restante (Ajustado a 0.16 para frenado rápido)
            //rowSpeeds.current[rowIndex] = Math.min(rowSpeeds.current[rowIndex], Math.max(4, distance * 0.16));
            rowSpeeds.current[rowIndex] = Math.min(rowSpeeds.current[rowIndex], Math.max(4, distance * 0.05));
        }

        rowPositions.current[rowIndex] -= rowSpeeds.current[rowIndex];

        // Loop infinito del rodillo
        if (rowPositions.current[rowIndex] <= -(sequenceWidth * 5)) {
            const jump = sequenceWidth * 4;
            rowPositions.current[rowIndex] += jump;
            if (rowStates.current[rowIndex] === 'STOPPING') {
                snapTargets.current[rowIndex] += jump;
            }
        }

        const strip = document.getElementById(`rollers-strip-${rowIndex}`);
        if (strip) {
            strip.style.transform = `translateX(${rowPositions.current[rowIndex]}px)`;
        }

        animationRefs.current[rowIndex] = requestAnimationFrame(() => animateReel(rowIndex, images));
    };

    const stopReel = (rowIndex) => {
        if (rowStates.current[rowIndex] === 'RUNNING') {
            const images = theme?.rollerImages || [];
            if (images.length > 0) {
                const currentPos = Math.abs(rowPositions.current[rowIndex]);
                const snapIndex = Math.ceil(currentPos / symbolWidth);
                
                // Reducimos el offset extra a + 3 símbolos para un frenado sumamente ágil e inmediato
                //let targetSymbolIndex = snapIndex + 3;
                let targetSymbolIndex = snapIndex;
                const targetImage = spinResultRef.current?.visualResult[rowIndex];
                
                if (images.includes(targetImage)) {
                    while (images[targetSymbolIndex % images.length] !== targetImage) {
                        targetSymbolIndex++;
                    }
                }

                snapTargets.current[rowIndex] = -(targetSymbolIndex * symbolWidth);
                rowStates.current[rowIndex] = 'STOPPING';
                currentStoppingReelRef.current = Math.max(currentStoppingReelRef.current, rowIndex + 1);
            }
        }
    };

    const finishSpin = () => {
        setIsPlaying(false);
        setMachineState('ended');
        startIdleLights(theme?.bulbColors);

        if (spinResultRef.current?.isWinner) {
            setStatusMessage('¡GANASTE!');
        } else {
            setStatusMessage('¡SUERTE PARA LA PRÓXIMA!');
        }
    };

    // ==========================================
    // DISPOSICIÓN DE FONDOS
    // ==========================================
    const renderBackground = () => {
        if (!theme) return <div className="rollers-screen-bg-color" style={{ backgroundColor: '#121212' }} />;

        if (theme.gameBackgroundType === 'video' && theme.gameBackgroundUrl) {
            return (
                <video 
                    src={getMediaUrl(theme.gameBackgroundUrl)} 
                    className="rollers-screen-bg-media" 
                    autoPlay loop muted playsInline 
                />
            );
        } else if (theme.gameBackgroundType === 'image' && theme.gameBackgroundUrl) {
            return (
                <img 
                    src={getMediaUrl(theme.gameBackgroundUrl)} 
                    className="rollers-screen-bg-media" 
                    alt="Background" 
                />
            );
        } else {
            return <div className="rollers-screen-bg-color" style={{ backgroundColor: theme.gameBackgroundColor || '#121212' }} />;
        }
    };

    const count = config?.rollersCount || 3;
    const images = theme?.rollerImages || [];

    // Calcular el alto de cada rodillo de forma dinámica
    const rowHeight = totalHeight / count;

    // Generar luces de la feria simétricamente
    const lights = Array.from({ length: 36 }).map((_, idx) => (
        <div key={idx} className="rollers-light-bulb" />
    ));

    return (
        <div className="rollers-screen-container">
            {renderBackground()}

            {/* Máquina principal de rodillos */}
            <div 
                className="rollers-machine-wrapper" 
                style={{ 
                    transform: `scale(${machineScale / 100})`, 
                    backgroundColor: theme?.machineBackgroundType === 'image' && theme.machineBackgroundUrl 
                        ? 'transparent' 
                        : (theme?.machineBackgroundColor || '#ffffff'),
                    backgroundImage: theme?.machineBackgroundType === 'image' && theme.machineBackgroundUrl
                        ? `url(${getMediaUrl(theme.machineBackgroundUrl)})`
                        : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}
            >
                {/* Luces del Marco de la Máquina */}
                <div className="rollers-lights-frame">
                    {lights}
                </div>

                {/* Logotipo Superior */}
                <div className="rollers-logo-container">
                    {theme?.brandLogoUrl ? (
                        <img 
                            src={getMediaUrl(theme.brandLogoUrl)} 
                            alt="Brand Logo" 
                            style={{ height: '110px' }}
                        />
                    ) : (
                        <h2 className="text-black font-extrabold text-4xl">¡SUERTE!</h2>
                    )}
                </div>

                {/* Caja de Rodillos (Rollette Wrapper) */}
                <div 
                    className="rollers-reels-box"
                    style={{ 
                        width: `${symbolWidth}px`, 
                        height: `${totalHeight}px`,
                        backgroundColor: '#fdf4e3' 
                    }}
                >
                    {Array.from({ length: count }).map((_, rowIndex) => (
                        <div 
                            key={rowIndex} 
                            className="rollers-row"
                            style={{ height: `${rowHeight}px` }}
                        >
                            {/* Tira de Símbolos deslizante */}
                            <div 
                                id={`rollers-strip-${rowIndex}`} 
                                className="rollers-strip"
                                style={{ height: `${rowHeight}px` }}
                            >
                                {images.length > 0 && Array.from({ length: symbolsPerStrip }).map((_, sIdx) => {
                                    const imgUrl = images[sIdx % images.length];
                                    return (
                                        <div 
                                            key={sIdx} 
                                            className="rollers-symbol flex justify-center relative overflow-hidden shrink-0"
                                            style={{ 
                                                width: `${symbolWidth}px`, 
                                                height: `${rowHeight}px` 
                                            }}
                                        >
                                            {/* Desplazamiento dinámico de la imagen gigante dentro del rodillo */}
                                            <div 
                                                className="rollers-character-wrapper absolute left-0"
                                                style={{ 
                                                    width: `${symbolWidth}px`, 
                                                    height: `${totalHeight}px`,
                                                    top: `-${rowIndex * rowHeight}px`
                                                }}
                                            >
                                                <img 
                                                    src={getMediaUrl(imgUrl)} 
                                                    alt="Character" 
                                                    className="w-full h-full object-cover" 
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Panel inferior LCD / Mensajes de estado */}
                <div className="rollers-lcd-panel mt-4 px-6 py-2 bg-black border-2 border-yellow-400 rounded-lg text-center shadow-lg">
                    <span className="text-yellow-400 font-mono text-xl tracking-widest fw-bold">
                        {statusMessage}
                    </span>
                </div>
            </div>

            {/* Identificar Pantalla */}
            {identifyData && (
                <div className="absolute top-10 left-10 p-6 bg-black border-4 border-yellow-400 rounded-lg text-white font-bold text-3xl z-50">
                    {identifyData}
                </div>
            )}
        </div>
    );
};
